import { inject, injectable } from '../../modules/common/di';
import { BaseService } from './baseService';
import { ApiResponseBase } from '../models/apiResponseBase';
import { AuthService } from './authService';
import { TagsApi } from '../api/admin/tags/tagsApi';
import { GetPopularTagsApiResponse, TagType } from '../api/admin/tags/popularTagsApiResponse';
import { TagCategory } from '../api/admin/usersAndLocations/getOrganizationLocationsApiResponse';
import { ApplyTagsModel } from '../api/admin/tags/applyTagsApiResponse';

@injectable('TagsService')
export class TagsService extends BaseService {
  @inject('AuthService') protected readonly authService!: AuthService;
  @inject('TagsApi') protected readonly tagsApi!: TagsApi;

  constructor() {
    super();
  }

  /**
   * Gets Organization's popular tags.
   * @param {number} organizationId If of the organization to get tags for.
   * @param {TagType} type Type of the tags to get.
   * @param {TagCategory} [category] Category of the tags to get.
   * @param {number} [limit] Limit the returned tags count by this number.
   * @returns {Promise<GetPopularTagsApiResponse>}
   */
  public getPopularTags(organizationId: number, type: TagType, category?: TagCategory, limit?: number): Promise<GetPopularTagsApiResponse> {
    if (organizationId < 1 || isNaN(organizationId)) {
      return this.reject(`Organization ID is incorrect [${organizationId}].`);
    }
    const params: {
      type: TagType;
      sortCollection: string;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      category?: TagCategory;
      limit?: number;
    } = {
      type: type,
      sortCollection: 'tags',
      sortBy: 'tag',
      sortOrder: 'asc',
    };

    if (limit != null && !isNaN(limit) && limit > 0) {
      params.limit = limit;
    }

    if (category !== undefined && !isNaN(category) && category >= 0) {
      params.category = category;
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.tagsApi.getPopularTags(organizationId, params));
  }

  /**
   * Allows to apply tags
   * @param {number} organizationId
   * @param {Array<{type: TagType; id: string | number; tag: string}>} tags
   * @returns {Promise<ApiResponseBase>}
   */
  public applyTags(
    organizationId: number,
    tags: ApplyTagsModel['tags'],
  ): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated()
      .then(() => this.tagsApi.applyTags(organizationId, {tags: tags}));
  }

  /**
   * Allows to delete specified tag for the specified entity type.
   * @param {number} organizationId
   * @param {TagType} type Type of the tag.
   * @param {string | number} id The ID of user, location or device.
   * @param {string} tag Tag value.
   * @param {number} appId Bot's instance ID to delete the bot-related tag.
   * @returns {Promise<ApiResponseBase>}
   */
  public deleteTags(
    organizationId: number,
    type: TagType,
    id: string | number,
    tag: string,
    appId?: number,
  ): Promise<ApiResponseBase> {
    const params: { type: TagType; id: string | number; tag: string; appId?: number } = {
      type: type,
      id: id,
      tag: tag,
      appId: appId,
    };

    return this.authService.ensureAuthenticated().then(() => this.tagsApi.deleteTags(organizationId, params));
  }
}
